import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../app/api/axiosError";
import { updateParcelCenter } from "../../../app/features/parcelCenter/parcelCenterSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { ParcelCenterResponse } from "../../../app/models/responseModels/parcelCenterResponse";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import { STATES_LIST } from "../../fdp/FdpConstants";
import AssociatedClientGrid from "../associated clients/AssociatedClientGrid";
import AssociatedFdpGrid from "../assoicated fdp/AssociatedFdpGrid";
import ParcelCenterContacts from "../associated Contacts/ParcelCenterContacts";

interface EditParcelCenterProps {
  parcelCenterData: ParcelCenterResponse;
  onCompleted: CallableFunction;
}

const EditParcelCenter: React.FC<EditParcelCenterProps> = ({
  parcelCenterData,
  onCompleted,
}) => {
  const { authResponse } = useSelector((state: RootState) => state.login);

  const [isFieldsReadOnly, setIsFieldsReadOnly] = useState<boolean>(true);

  const dispatch = useAppDispatch();

  const initialValues = {
    parcelCenterId: parcelCenterData.parcelCenterId,
    parcelCenterName: parcelCenterData.parcelCenterName,
    address: parcelCenterData.address,
    city: parcelCenterData.city,
    state: parcelCenterData.state,
    zipCode: parcelCenterData.zipCode,
    latitude: parcelCenterData.latitude,
    longitude: parcelCenterData.longitude,
    timezone: parcelCenterData.timezone,
    createdOn: parcelCenterData.createdOn,
    updatedOn: parcelCenterData.updatedOn,
    createdBy: authResponse?.userSettings?.userName ?? "",
    updatedBy: authResponse?.userSettings?.userName ?? "",
  };

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    parcelCenterName: Yup.string().required("Parcel center name is required"),
    address: Yup.string().required("Address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    zipCode: Yup.string().required("Zip code is required"),
    timezone: Yup.string().required("Timezone is required"),
  });

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      console.log(values);

      const response = await dispatch(updateParcelCenter(values)).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "parcelCenterList",
        });
      }
      toast.success("Parcel center information has been updated successfully.");
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "parcelCenterListException",
      });
    } finally {
      onCompleted();
    }
    return true;
  };

  const handleCancel = (resetForm: FormikHelpers<any>["resetForm"]) => {
    resetForm();
    setIsFieldsReadOnly(true);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          setSubmitting(true);
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting, values, setFieldValue, resetForm }) => (
          <Form>
            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid container item xs={12} justifyContent="space-between">
                <Grid item>
                  <Typography variant="h5" component="h3">
                    Parcel Center Details:
                  </Typography>
                </Grid>
                <Grid item>
                  {isFieldsReadOnly && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setIsFieldsReadOnly(false)}
                    >
                      Edit
                    </Button>
                  )}
                  {!isFieldsReadOnly && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => handleCancel(resetForm)}
                        style={{ marginRight: "5px" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isSubmitting}
                        type="submit"
                      >
                        Submit
                      </Button>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="parcelCenterId"
                  label="Parcel Center ID"
                  name="parcelCenterId"
                  size="small"
                  fullWidth
                  required
                  variant={"filled"}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="parcelCenterName"
                  label="Parcel Center Name"
                  name="parcelCenterName"
                  size="small"
                  fullWidth
                  required
                  variant={"filled"}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="address"
                  label="Address"
                  name="address"
                  size="small"
                  fullWidth
                  required
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="city"
                  label="City"
                  name="city"
                  size="small"
                  fullWidth
                  required
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  select
                  margin="normal"
                  id="state"
                  label="State"
                  name="state"
                  size="small"
                  fullWidth
                  required
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: { style: { maxHeight: 150 } },
                    },
                  }}
                >
                  {STATES_LIST.map((state: string, index: number) => (
                    <MenuItem key={index} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </FormikTextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="zipCode"
                  label="Zip Code"
                  name="zipCode"
                  size="small"
                  fullWidth
                  required
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="latitude"
                  label="Latitude"
                  name="latitude"
                  size="small"
                  fullWidth
                  variant={"filled"}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="longitude"
                  label="Longitude"
                  name="longitude"
                  size="small"
                  fullWidth
                  variant={"filled"}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="timezone"
                  label="Timezone"
                  name="timezone"
                  size="small"
                  fullWidth
                  required
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
      <Card style={{ marginTop: "1%" }}>
        <CardHeader subheader="Contacts" />
        <CardContent>
          <Grid container columnSpacing={2}>
            <ParcelCenterContacts
              parcelCenterId={parcelCenterData.parcelCenterId}
              data={parcelCenterData.pcContacts || []}
            />
          </Grid>
        </CardContent>
      </Card>
      <Card style={{ marginTop: "1%" }}>
        <CardHeader subheader="Associated Fdp(s)" />
        <CardContent>
          <Grid container columnSpacing={2}>
            <AssociatedFdpGrid
              data={parcelCenterData.fdps}
              parcelCenterData={parcelCenterData}
              onComplete={onCompleted}
            />
          </Grid>
        </CardContent>
      </Card>
      <Card style={{ marginTop: "1%" }}>
        <CardHeader subheader="Associated Client(s)" />
        <CardContent>
          <Grid container columnSpacing={2}>
            <AssociatedClientGrid data={parcelCenterData.clients} />
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default EditParcelCenter;
